import { useState, useEffect } from 'react';
import { Language } from '../types';
import { motion, AnimatePresence } from 'motion/react';

interface LanguageSwitcherProps {
  language: Language;
  setLanguage: (lang: Language) => void;
  className?: string;
}

const GlobeIcon = () => (
  <div className="relative w-8 h-8 rounded-full overflow-hidden bg-[#1a365d] border border-slate-500 shadow-inner group cursor-pointer hover:border-blue-400">
    {/* Deep blue sea - base layer */}
    <div className="absolute inset-0 bg-[#1a365d]"></div>
    
    {/* Rotating Surface - Moves West to East (Surface moves Right) */}
    <motion.div 
      className="flex h-full"
      animate={{ x: ['-50%', '0%'] }}
      transition={{ 
        duration: 10, 
        repeat: Infinity, 
        ease: "linear" 
      }}
      style={{ width: '200%' }}
    >
      {[0, 1].map((i) => (
        <div key={i} className="h-full w-1/2 shrink-0 relative flex items-center">
          <svg viewBox="0 0 100 50" className="h-[80%] w-full" preserveAspectRatio="none">
            {/* Americas - Centered between 10 and 35 */}
            <path 
              d="M10,10 C15,8 18,12 18,20 C18,30 15,35 12,38 C8,40 5,35 5,20 C5,10 8,12 10,10 Z M25,5 Q30,8 30,15 T25,25 Q20,22 25,5 Z" 
              fill="#2d6a4f" 
            />
            {/* Eurasia & Africa - Centered between 45 and 85 */}
            <path 
              d="M45,15 Q55,10 65,15 T80,12 T90,20 T85,35 T70,42 T55,38 T45,15 Z" 
              fill="#2d6a4f" 
            />
            {/* Australia - Small detail at 80 */}
            <path 
              d="M80,35 Q84,32 86,35 T84,40 T80,38 Z" 
              fill="#2d6a4f" 
            />
            {/* Antarctica - Bottom strip */}
            <path 
              d="M0,48 Q50,47 100,48 L100,50 L0,50 Z" 
              fill="#f8fafc" 
              opacity="0.9"
            />
          </svg>
        </div>
      ))}
    </motion.div>
    
    {/* 3D Lighting & Atmosphere Overlays */}
    {/* Atmosphere glow at edges */}
    <div className="absolute -inset-[0.5px] rounded-full ring-[1px] ring-blue-400/30 blur-[0.5px] pointer-events-none"></div>
    {/* Spherical Shadow */}
    <div className="absolute inset-0 pointer-events-none rounded-full shadow-[inset_-6px_-6px_12px_rgba(0,0,0,0.6),inset_6px_6px_12px_rgba(255,255,255,0.25)]"></div>
    {/* Center highlight */}
    <div className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-black/40 via-transparent to-white/10"></div>
  </div>
);

const LanguageSwitcher = ({ language, setLanguage, className = '' }: LanguageSwitcherProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const baseClasses = 'px-1 py-1 md:px-2 md:py-1 text-[8px] min-[360px]:text-[9px] min-[400px]:text-[10px] md:text-xs font-bold transition-all duration-200 cursor-pointer min-w-[20px] min-[360px]:min-w-[24px] min-[400px]:min-w-[28px] md:min-w-[32px] text-center flex items-center justify-center';
  const activeClasses = 'bg-blue-600 text-white shadow-inner';
  const inactiveClasses = 'bg-slate-800 text-slate-400 hover:text-slate-200 hover:bg-slate-700';

  const languages: { code: Language; label: string; fullName: string }[] = [
    { code: 'es', label: 'ES', fullName: 'Español' },
    { code: 'en', label: 'EN', fullName: 'English' },
    { code: 'fr', label: 'FR', fullName: 'Français' },
    { code: 'ru', label: 'RU', fullName: 'Русский' },
    { code: 'ua', label: 'UA', fullName: 'Українська' },
    { code: 'cat', label: 'CAT', fullName: 'Català' }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const handleOutsideClick = () => setIsMobileMenuOpen(false);
    window.addEventListener('click', handleOutsideClick);
    return () => window.removeEventListener('click', handleOutsideClick);
  }, [isMobileMenuOpen]);

  return (
    <div className={`relative ${className}`}>
      {/* 1. DESKTOP VIEW: Horizontal row, always visible on md+ */}
      <div className="hidden md:flex flex-row rounded-md overflow-hidden border border-slate-700/50 bg-slate-900/50 backdrop-blur-sm shadow-md">
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => setLanguage(lang.code)}
            className={`${baseClasses} ${language === lang.code ? activeClasses : inactiveClasses}`}
            aria-pressed={language === lang.code}
          >
            {lang.label}
          </button>
        ))}
      </div>

      {/* 2. MOBILE VIEW: Rotating Globe Button with vertical absolute dropdown */}
      <div className="md:hidden">
        <button
          onClick={(e) => {
            e.stopPropagation(); // prevent immediate close
            setIsMobileMenuOpen(!isMobileMenuOpen);
          }}
          className="flex items-center justify-center p-1 rounded-full bg-slate-800/90 border border-slate-600 shadow-xl hover:bg-slate-700 transition-colors cursor-pointer"
          title="Change language"
        >
          <GlobeIcon />
        </button>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.95 }}
              transition={{ duration: 0.12 }}
              className="absolute right-0 mt-2 bg-slate-900/95 backdrop-blur-md border border-slate-700 rounded-xl shadow-2xl overflow-hidden py-1 min-w-[120px] z-[100] flex flex-col divide-y divide-slate-800/40"
            >
              <div className="px-3 py-1 text-[8px] font-bold text-slate-500 uppercase tracking-wider text-center bg-slate-950/40">
                Idioma / Language
              </div>
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    setLanguage(lang.code);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`px-4 py-2.5 text-xs font-semibold text-left transition-colors flex items-center justify-between ${
                    language === lang.code
                      ? 'bg-blue-600/20 text-blue-400 font-bold'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <span>{lang.fullName}</span>
                  {language === lang.code && (
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                  )}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LanguageSwitcher;
